import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isValid, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { usePermissions } from '@/contexts/PermissionContext';

export type TimesheetDeepLink = {
  companyId?: string;
  employeeId: number;
  weekEnding: Date;
};

/** Parses `?companyId=&employeeId=&weekEnding=yyyy-MM-dd`; returns null when it is not a timesheet link. */
export function parseTimesheetDeepLink(search: string): TimesheetDeepLink | null {
  const params = new URLSearchParams(search);
  const employeeId = Number(params.get('employeeId'));
  const weekEndingParam = params.get('weekEnding');
  if (!Number.isInteger(employeeId) || employeeId <= 0 || !weekEndingParam) return null;

  const weekEnding = parseISO(weekEndingParam.slice(0, 10));
  if (!isValid(weekEnding)) return null;

  return { companyId: params.get('companyId') || undefined, employeeId, weekEnding };
}

/**
 * Opens the timesheet linked to by another product (e.g. Projects' "View in Access Time"):
 * `/timesheet?companyId=&employeeId=&weekEnding=yyyy-MM-dd`. Switches to the linked company,
 * checks the user may view that person's timesheets, then hands off to the report drilldown.
 */
export function useTimesheetDeepLink(
  onOpen: (drilldown: { employeeId: number; weekEnding: Date }) => void,
): void {
  const location = useLocation();
  const { currentCompany, availableCompanies, isLoading: companiesLoading, switchCompany } = useCompany();
  const { isLoading: permissionsLoading, canViewTimesheet } = usePermissions();
  const handledSearch = useRef<string | null>(null);
  const switchingTo = useRef<string | null>(null);

  useEffect(() => {
    const link = location.pathname === '/timesheet' ? parseTimesheetDeepLink(location.search) : null;
    if (!link) {
      // Forget the last link once it has been consumed, so following the same link again still opens it.
      handledSearch.current = null;
      switchingTo.current = null;
      return;
    }
    if (handledSearch.current === location.search || companiesLoading) return;

    if (link.companyId && currentCompany?.id !== link.companyId) {
      if (!availableCompanies.some((c) => c.id === link.companyId)) {
        handledSearch.current = location.search;
        toast.error("You don't have access to the company this timesheet belongs to.");
        return;
      }
      if (switchingTo.current !== link.companyId) {
        switchingTo.current = link.companyId;
        const search = location.search;
        switchCompany(link.companyId).catch(() => {
          switchingTo.current = null;
          handledSearch.current = search;
          toast.error("Couldn't switch to the company this timesheet belongs to. Please try again.");
        });
      }
      return;
    }

    if (permissionsLoading) return;
    handledSearch.current = location.search;

    if (!canViewTimesheet(link.employeeId)) {
      toast.error("You don't have permission to view this person's timesheet.");
      return;
    }
    onOpen({ employeeId: link.employeeId, weekEnding: link.weekEnding });
  }, [
    location.pathname,
    location.search,
    companiesLoading,
    currentCompany?.id,
    availableCompanies,
    switchCompany,
    permissionsLoading,
    canViewTimesheet,
    onOpen,
  ]);
}
